import React, { FC, useRef, useState, useEffect } from 'react'
import cn from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import s from './DropdownMenu.module.css'
import { User } from '@components/icons'
import { useUI } from '@corratech/pylot-ui/context'
import { ClickOutside } from '@corratech/pylot-utils'
import { useTranslation } from 'next-i18next'
import { useUser } from '@corratech/pylot-auth-manager'
import { useLogout } from '@components/alfakher/pylot-auth-manager'
import { useStoreConfig } from '@config/hooks/useStoreConfig'

import {
    disableBodyScroll,
    enableBodyScroll,
    clearAllBodyScrollLocks
} from 'body-scroll-lock'

interface DropdownMenuProps {
    open?: boolean
}

const DropdownMenu: FC<DropdownMenuProps> = () => {
    const { t } = useTranslation('common')
    const { logout } = useLogout()
    const { pathname } = useRouter()
    const [display, setDisplay] = useState(false)
    const { closeSidebarIfPresent } = useUI()
    const ref = useRef() as React.MutableRefObject<HTMLUListElement>
    const { customer } = useUser()
    const config = useStoreConfig()
    const LINKS = [
        {
            name: `${t('My Account')}`,
            href: '/account',
            enable: true
        },
        {
            name: `${t('Wish List')}`,
            href: '/account/wishlist',
            enable: config?.base?.wishlist?.enabled
        },
        {
            name: `${t('View Orders')}`,
            href: '/account/orders',
            enable: true
        }
    ]

    useEffect(() => {
        if (ref.current) {
            if (display) {
                disableBodyScroll(ref.current)
            } else {
                enableBodyScroll(ref.current)
            }
        }
        return () => {
            clearAllBodyScrollLocks()
        }
    }, [display])

    return (
        <ClickOutside active={display} onClick={() => setDisplay(false)}>
            <div className="flex">
                <button
                    className={s.avatarButton}
                    onClick={() => setDisplay(!display)}
                    aria-label={t('auth|Menu')}
                >
                    <User />
                </button>
                {display && (
                    <ul
                        className={cn(s['dropdownMenu'], 'animate-fade-in-up')}
                        ref={ref}
                    >
                        <li className={s['welcome-message']}>
                            {`${t('Hi {{name}}!', {
                                name: customer?.firstname
                            })}`}
                        </li>
                        {LINKS.map(({ name, href, enable }) => (
                            <>
                                {enable && (
                                    <li key={name + href}>
                                        <div>
                                            <Link href={href} passHref>
                                                <a
                                                    href="/#"
                                                    className={cn(
                                                        s['userLink'],
                                                        'no-underline'
                                                    )}
                                                >
                                                    <span
                                                        className={cn(s.link, {
                                                            [s.active]:
                                                                pathname ===
                                                                href
                                                        })}
                                                        onClick={() => {
                                                            setDisplay(false)
                                                            closeSidebarIfPresent()
                                                        }}
                                                        aria-hidden="true"
                                                    >
                                                        {name}
                                                    </span>
                                                </a>
                                            </Link>
                                        </div>
                                    </li>
                                )}
                            </>
                        ))}
                        <li>
                            <span
                                className={cn(s.link)}
                                onClick={() => {
                                    setDisplay(false)
                                    logout()
                                }}
                                aria-hidden="true"
                            >
                                {t('sign out')}
                            </span>
                        </li>
                    </ul>
                )}
            </div>
        </ClickOutside>
    )
}

export default DropdownMenu
